/**
 * Reviews — the Adviser's annual Review Planner.
 *
 * A year grid of 12 month cards (Jan and Dec closed by design) holding
 * review clients, with drag-and-drop between open months, an atwork
 * .xlsx month-list import, manual add, a per-client checklist modal,
 * the daily nudge, and — per CLAUDE.md §34.10 — client GROUPS (one
 * review meeting unit) with file-explorer-style multi-select and bulk
 * actions.
 *
 * HARD CONTRACT (§34.7): this surface is fully self-contained on the
 * review_clients / review_groups tables. It never imports from, writes
 * to, or subscribes to file statistics, prep-request statistics, the
 * bonus counter, or SLA logic.
 *
 * The route is Adviser-only (AdviserOnlyRoute in App.jsx) and the nav
 * item is hidden from both assistant roles (Header.jsx).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarCheck, ChevronLeft, ChevronRight, Upload, UserPlus, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import useAutoRefresh from '../hooks/useAutoRefresh';
import {
  getReviewClientsForYear,
  getActiveReviewClients,
  getReviewGroups,
  createReviewGroup,
  renameReviewGroup,
  deleteReviewGroup,
  setClientsGroup,
  updateReviewClient,
  updateReviewClientsExpanded,
  deleteReviewClient,
  deleteReviewClients,
} from '../api/reviewClientsApi';
import {
  MONTH_NAMES,
  OPEN_MONTHS,
  isOpenMonth,
  getCurrentSASTYearMonth,
  formatClientListName,
  sortMonthClients,
  groupFallbackLabel,
} from '../utils/reviewPlanner';
import MonthCard from '../components/reviews/MonthCard';
import ImportMonthListModal from '../components/reviews/ImportMonthListModal';
import AddClientModal from '../components/reviews/AddClientModal';
import ClientDetailModal from '../components/reviews/ClientDetailModal';
import { ReviewNudgeBanner } from '../components/reviews/ReviewNudge';
import '../styles/reviews.css';

// Load Montserrat once for this surface (same Google Fonts link-injection
// pattern as the Tools fee calculator — the app-wide fonts are unchanged).
const FONT_LINK_ID = 'rv-montserrat-font';
function ensureMontserrat() {
  if (typeof document === 'undefined') return;
  if (!document.getElementById(FONT_LINK_ID)) {
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }
}

export default function Reviews() {
  const sastNow = getCurrentSASTYearMonth();
  const [year, setYear] = useState(sastNow.year);
  const [clients, setClients] = useState([]);
  // Active (non-completed) clients across ALL years — feeds the daily
  // nudge banner, which always reflects the current SAST month
  // regardless of which planner year is being viewed (§34.9).
  const [activeClients, setActiveClients] = useState([]);
  // review_groups labels, keyed by group id (§34.10).
  const [groupLabels, setGroupLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState(null); // { message, isError }
  const [showImport, setShowImport] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  // Multi-select (§34.10): selected client ids; selection mode is
  // simply "any selection exists".
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  // Currently dragged unit: 'client:<id>' or 'group:<id>'.
  const [draggingKey, setDraggingKey] = useState(null);

  // Latest year for the auto-refresh callback (which must not re-bind).
  const yearRef = useRef(year);
  yearRef.current = year;
  const toastTimerRef = useRef(null);

  useEffect(() => { ensureMontserrat(); }, []);
  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // Esc clears the selection (file-explorer behaviour).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedIds(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const showToast = useCallback((message, isError = false) => {
    clearTimeout(toastTimerRef.current);
    setToast({ message, isError });
    toastTimerRef.current = setTimeout(() => setToast(null), 3200);
  }, []);

  /**
   * Load one planner year (+ group labels + the cross-year active set
   * for the nudge). Read-only: page load performs ZERO writes to
   * review_clients — review_month is a permanent, Adviser-owned anchor
   * (§34.4), so there is no rollover or reconciliation of any kind.
   */
  const load = useCallback(async (targetYear, { silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setLoadError('');
    }
    try {
      const [rows, active, labels] = await Promise.all([
        getReviewClientsForYear(targetYear),
        getActiveReviewClients().catch(() => null), // nudge degrades gracefully
        getReviewGroups().catch(() => null), // group labels degrade to fallbacks
      ]);

      // Ignore a stale response if the user switched years mid-flight.
      if (yearRef.current !== targetYear) return;
      setClients(rows);
      if (active) setActiveClients(active);
      if (labels) setGroupLabels(labels);
    } catch (err) {
      if (silent) throw err; // let useAutoRefresh log it silently
      console.error('[reviews] load failed:', err);
      setLoadError(err.message || 'Could not load the review planner.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(year); }, [year, load]);

  // Silent background refresh: tab-focus refetch + 45s polling.
  useAutoRefresh(useCallback(() => load(yearRef.current, { silent: true }), [load]));

  // ---- Local state helpers ----

  const applyLocal = (id, patch) => {
    setClients((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  /** Merge server-confirmed rows into both local lists. */
  const mergeRows = (rows) => {
    const byId = {};
    rows.forEach((r) => { byId[r.id] = r; });
    setClients((prev) => prev.map((c) => byId[c.id] || c));
    setActiveClients((prev) => prev.map((c) => byId[c.id] || c));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const toggleSelect = (clientId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const toggleSelectGroup = (memberIds, select) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      memberIds.forEach((id) => (select ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const selectedClients = clients.filter((c) => selectedIds.has(c.id));

  // ---- Moves (always via the group-expanding API, §34.10) ----

  /**
   * Move any set of clients to another open month. The API expands the
   * set to whole groups, so a grouped member always drags its group
   * along — the same-month invariant can never break.
   */
  const moveByIds = async (ids, newMonth, describedAs) => {
    if (!isOpenMonth(newMonth) || ids.length === 0) return;
    try {
      const updated = await updateReviewClientsExpanded(ids, { reviewMonth: newMonth });
      mergeRows(updated);
      showToast(`${describedAs} moved to ${MONTH_NAMES[newMonth]}`);
    } catch (err) {
      showToast(err.message || 'Could not move.', true);
      load(yearRef.current, { silent: true }).catch(() => {});
    }
  };

  /** Single-unit drag-and-drop / detail-modal month select. */
  const moveClient = (client, newMonth) => {
    if (newMonth === client.reviewMonth) return;
    moveByIds([client.id], newMonth, formatClientListName(client));
  };

  const handleDropUnit = ({ clientId, groupId }, month) => {
    setDraggingKey(null);
    if (clientId) {
      const client = clients.find((c) => c.id === clientId);
      if (client) moveClient(client, month);
    } else if (groupId) {
      const members = clients.filter((c) => c.groupId === groupId);
      if (members.length === 0 || members[0].reviewMonth === month) return;
      moveByIds([members[0].id], month, groupLabels[groupId] || 'Group');
    }
  };

  const bulkMove = (month) => {
    const count = selectedIds.size;
    if (count === 0) return;
    moveByIds([...selectedIds], month, `${count} selected`);
    clearSelection();
  };

  // ---- Group / Ungroup / Delete (§34.10) ----

  /** Dissolve any group left with fewer than 2 members: clear the
   *  remaining member's group_id and delete the review_groups row. */
  const dissolveUndersized = async (groupIds, clientsAfter) => {
    for (const gid of new Set(groupIds.filter(Boolean))) {
      const remaining = clientsAfter.filter((c) => c.groupId === gid);
      if (remaining.length >= 2) continue;
      if (remaining.length === 1) await setClientsGroup([remaining[0].id], null);
      await deleteReviewGroup(gid).catch(() => {});
      setGroupLabels((prev) => {
        const next = { ...prev };
        delete next[gid];
        return next;
      });
    }
  };

  const groupSelected = async () => {
    const sel = selectedClients;
    if (sel.length < 2) {
      showToast('Select at least two clients to group.', true);
      return;
    }
    const months = new Set(sel.map((c) => `${c.reviewYear}-${c.reviewMonth}`));
    if (months.size > 1) {
      showToast('Grouped clients must be in the same month — move them together first.', true);
      return;
    }
    if (sel.some((c) => c.status !== 'planned')) {
      showToast('Only planned clients can be grouped — pipeline reviews must complete first.', true);
      return;
    }
    try {
      const { individuals, entities } = sortMonthClients(sel);
      const label = groupFallbackLabel([...individuals, ...entities]);
      const group = await createReviewGroup(label);
      const oldGroupIds = sel.map((c) => c.groupId).filter(Boolean);
      await setClientsGroup(sel.map((c) => c.id), group.id);

      const after = clients.map((c) =>
        selectedIds.has(c.id) ? { ...c, groupId: group.id } : c
      );
      await dissolveUndersized(oldGroupIds, after);

      setGroupLabels((prev) => ({ ...prev, [group.id]: label }));
      clearSelection();
      showToast(`Group "${label}" created`);
      load(yearRef.current, { silent: true }).catch(() => {});
    } catch (err) {
      showToast(err.message || 'Could not create the group.', true);
      load(yearRef.current, { silent: true }).catch(() => {});
    }
  };

  const ungroupSelected = async () => {
    const grouped = selectedClients.filter((c) => c.groupId);
    if (grouped.length === 0) {
      showToast('No grouped clients selected.', true);
      return;
    }
    if (grouped.some((c) => c.status !== 'planned')) {
      showToast('Only planned clients can be ungrouped — pipeline reviews must complete first.', true);
      return;
    }
    try {
      const affectedGroups = grouped.map((c) => c.groupId);
      await setClientsGroup(grouped.map((c) => c.id), null);
      const after = clients.map((c) =>
        selectedIds.has(c.id) ? { ...c, groupId: null } : c
      );
      await dissolveUndersized(affectedGroups, after);
      clearSelection();
      showToast(`${grouped.length} client${grouped.length === 1 ? '' : 's'} ungrouped`);
      load(yearRef.current, { silent: true }).catch(() => {});
    } catch (err) {
      showToast(err.message || 'Could not ungroup.', true);
      load(yearRef.current, { silent: true }).catch(() => {});
    }
  };

  const requestBulkDelete = () => {
    // BLOCKED for in-flight reviews — complete them first (§34.10).
    const inPipeline = selectedClients.find(
      (c) => c.status === 'submitted' || c.status === 'appointment_made'
    );
    if (inPipeline) {
      showToast(`${formatClientListName(inPipeline)} is in the pipeline — complete the review first`, true);
      return;
    }
    if (selectedClients.length === 0) return;
    setConfirmBulkDelete(true);
  };

  const confirmBulkDeleteNow = async () => {
    setConfirmBulkDelete(false);
    const ids = [...selectedIds];
    const affectedGroups = selectedClients.map((c) => c.groupId).filter(Boolean);
    try {
      await deleteReviewClients(ids);
      const after = clients.filter((c) => !selectedIds.has(c.id));
      await dissolveUndersized(affectedGroups, after);
      setClients(after);
      setActiveClients((prev) => prev.filter((c) => !selectedIds.has(c.id)));
      clearSelection();
      showToast(`${ids.length} client${ids.length === 1 ? '' : 's'} removed from planner`);
      load(yearRef.current, { silent: true }).catch(() => {});
    } catch (err) {
      showToast(err.message || 'Could not delete.', true);
      load(yearRef.current, { silent: true }).catch(() => {});
    }
  };

  const handleRenameGroup = async (unit) => {
    // Small prompt (matches the app's lightweight confirm patterns) —
    // groups are renamed rarely.
    // eslint-disable-next-line no-alert
    const next = window.prompt('Group name:', unit.label);
    if (next === null) return;
    const label = next.trim();
    if (!label || label === unit.label) return;
    try {
      await renameReviewGroup(unit.groupId, label);
      setGroupLabels((prev) => ({ ...prev, [unit.groupId]: label }));
      showToast(`Group renamed to "${label}"`);
    } catch (err) {
      showToast(err.message || 'Could not rename the group.', true);
    }
  };

  // ---- Checklist / detail-modal / pipeline actions ----

  /** Persist a checklist/applicability patch with optimistic UI
   *  (per-member — never propagates across a group). */
  const patchClient = async (client, patch) => {
    const revert = { ...client };
    applyLocal(client.id, patch);
    try {
      await updateReviewClient(client.id, patch);
    } catch (err) {
      applyLocal(client.id, revert);
      showToast(err.message || 'Could not save the change.', true);
    }
  };

  /** Save an edited client name (§34.2) — PER-CLIENT, never
   *  group-expanded (names are individual; group labels have their own
   *  rename). Optimistic; sorting and RELATED? hints recompute on the
   *  next render from the updated fields. Names already snapshotted
   *  into a sent prep request's member_sections are deliberately not
   *  touched (§34.11 — the request records what was sent). */
  const saveClientName = async (client, patch) => {
    const revert = { ...client };
    applyLocal(client.id, patch);
    setActiveClients((prev) => prev.map((c) => (c.id === client.id ? { ...c, ...patch } : c)));
    try {
      await updateReviewClient(client.id, patch);
      showToast('Name updated');
      return true;
    } catch (err) {
      applyLocal(client.id, revert);
      setActiveClients((prev) => prev.map((c) => (c.id === client.id ? revert : c)));
      showToast(err.message || 'Could not update the name.', true);
      return false;
    }
  };

  /** Hard-delete a single planner row (Adviser-only exception, §34.6). */
  const removeClient = async (client) => {
    setSelectedClientId(null);
    try {
      await deleteReviewClient(client.id);
      const after = clients.filter((c) => c.id !== client.id);
      await dissolveUndersized([client.groupId], after);
      setClients(after);
      setActiveClients((prev) => prev.filter((c) => c.id !== client.id));
      showToast(`${formatClientListName(client)} removed from planner`);
    } catch (err) {
      showToast(err.message || 'Could not remove the client.', true);
      load(yearRef.current, { silent: true }).catch(() => {});
    }
  };

  /**
   * Pull a unit into the pipeline (§34.9/§34.10): every member (the API
   * expands grouped ids) gets status 'submitted' + ONE shared
   * submitted_at, so a group renders one calendar card.
   */
  const addToPipelineByIds = async (ids, label) => {
    const patch = { status: 'submitted', submittedAt: new Date().toISOString() };
    try {
      const updated = await updateReviewClientsExpanded(ids, patch);
      mergeRows(updated);
      showToast(`Calendar card created — ${label} added to the pipeline`);
    } catch (err) {
      showToast(err.message || 'Could not add to the pipeline.', true);
      load(yearRef.current, { silent: true }).catch(() => {});
    }
  };

  /** From the client detail modal — a grouped client pulls its whole
   *  group (one card). */
  const addToPipeline = (client) => {
    const label = client.groupId
      ? groupLabels[client.groupId] || formatClientListName(client)
      : client.displayName;
    addToPipelineByIds([client.id], label);
  };

  /** From the nudge (banner or compact) — units carry their member ids. */
  const addUnitToPipeline = (unit) => {
    addToPipelineByIds(unit.memberIds, unit.isGroup ? unit.label : unit.displayName);
  };

  // ---- Drag and drop ----

  const onDragStartUnit = (e, key) => {
    e.dataTransfer.setData('text/plain', key);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingKey(key);
  };

  // ---- Derived ----

  const clientsByMonth = {};
  for (const c of clients) {
    (clientsByMonth[c.reviewMonth] = clientsByMonth[c.reviewMonth] || []).push(c);
  }

  const existingNames = new Set(
    clients.map((c) => c.displayName.trim().toLowerCase())
  );

  // Default month for the add/import modals: the current SAST month when
  // it is open and we're viewing the current year, otherwise the next
  // open month (January → February; December wraps to February).
  const defaultMonth = (() => {
    if (year !== sastNow.year) return OPEN_MONTHS[0];
    if (isOpenMonth(sastNow.month)) return sastNow.month;
    return OPEN_MONTHS.find((m) => m > sastNow.month) || OPEN_MONTHS[0];
  })();

  const selectedClient = clients.find((c) => c.id === selectedClientId) || null;

  return (
    <div className="rv-root">
      <div className="rv-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h2 className="rv-title"><CalendarCheck size={20} /> Review Planner</h2>
          <div className="rv-year-selector">
            <button className="rv-year-btn" onClick={() => setYear((y) => y - 1)} aria-label="Previous year">
              <ChevronLeft size={16} />
            </button>
            <span className="rv-year-value">{year}</span>
            <button className="rv-year-btn" onClick={() => setYear((y) => y + 1)} aria-label="Next year">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        <div className="rv-header-actions">
          <button className="rv-btn rv-btn-outline" onClick={() => setShowImport(true)}>
            <Upload size={14} /> Import month list
          </button>
          <button className="rv-btn rv-btn-primary" onClick={() => setShowAdd(true)}>
            <UserPlus size={14} /> Add client
          </button>
        </div>
      </div>

      {loadError && <div className="rv-error">{loadError}</div>}

      {/* Daily nudge (§34.9/§34.10) — counts review MEETINGS (units). */}
      <ReviewNudgeBanner
        clients={activeClients}
        groupLabels={groupLabels}
        onAddToPipeline={addUnitToPipeline}
      />

      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--cp-text-muted)', fontSize: '0.85rem' }}>
          Loading review planner…
        </div>
      ) : (
        <div className="rv-month-grid">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
            <MonthCard
              key={month}
              month={month}
              clients={clientsByMonth[month] || []}
              groupLabels={groupLabels}
              onOpenClient={(client) => setSelectedClientId(client.id)}
              onDropUnit={handleDropUnit}
              onRenameGroup={handleRenameGroup}
              draggingKey={draggingKey}
              onDragStartUnit={onDragStartUnit}
              onDragEndUnit={() => setDraggingKey(null)}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectGroup={toggleSelectGroup}
            />
          ))}
        </div>
      )}

      {/* Floating multi-select action bar (§34.10). */}
      {selectedIds.size > 0 && (
        <div className="rv-action-bar" role="toolbar" aria-label="Selection actions">
          <span className="rv-action-count">{selectedIds.size} selected</span>
          <select
            className="rv-select"
            value=""
            onChange={(e) => {
              const m = Number(e.target.value);
              if (m) bulkMove(m);
            }}
            aria-label="Move selection to month"
          >
            <option value="">Move to month…</option>
            {OPEN_MONTHS.map((m) => (
              <option key={m} value={m}>{MONTH_NAMES[m]} {year}</option>
            ))}
          </select>
          <button className="rv-btn rv-btn-outline" onClick={groupSelected}>Group</button>
          <button className="rv-btn rv-btn-outline" onClick={ungroupSelected}>Ungroup</button>
          <button className="rv-btn rv-btn-danger" onClick={requestBulkDelete}>
            <Trash2 size={13} /> Delete
          </button>
          <button className="rv-btn rv-btn-outline" onClick={clearSelection}>Clear</button>
        </div>
      )}

      {/* Bulk delete confirmation (planner deletion exception, §34.6). */}
      {confirmBulkDelete && (
        <div className="rv-overlay" onClick={() => setConfirmBulkDelete(false)}>
          <div className="rv-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Confirm delete">
            <h3 className="rv-modal-title"><Trash2 size={17} /> Delete {selectedIds.size} client{selectedIds.size === 1 ? '' : 's'}?</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--cp-text-muted)', marginBottom: '1rem' }}>
              This permanently removes {selectedIds.size === 1 ? 'this planner entry' : 'these planner entries'} —
              planner rows are planning data, so client files and CRM history are untouched. This cannot be undone.
            </p>
            <div className="rv-modal-actions">
              <button className="rv-btn rv-btn-outline" onClick={() => setConfirmBulkDelete(false)}>Cancel</button>
              <button className="rv-btn rv-btn-danger" onClick={confirmBulkDeleteNow}>Delete permanently</button>
            </div>
          </div>
        </div>
      )}

      {showImport && (
        <ImportMonthListModal
          year={year}
          defaultMonth={defaultMonth}
          existingNames={existingNames}
          onClose={() => setShowImport(false)}
          onImported={(imported, month) => {
            setShowImport(false);
            setClients((prev) => [...prev, ...imported]);
            setActiveClients((prev) => [...prev, ...imported]);
            showToast(
              `${imported.length} client${imported.length === 1 ? '' : 's'} imported to ${MONTH_NAMES[month]}`
            );
          }}
        />
      )}

      {showAdd && (
        <AddClientModal
          year={year}
          defaultMonth={defaultMonth}
          onClose={() => setShowAdd(false)}
          onAdded={(created) => {
            setShowAdd(false);
            setClients((prev) => [...prev, created]);
            setActiveClients((prev) => [...prev, created]);
            showToast(`${formatClientListName(created)} added to ${MONTH_NAMES[created.reviewMonth]}`);
          }}
        />
      )}

      {selectedClient && (
        <ClientDetailModal
          client={selectedClient}
          groupLabel={selectedClient.groupId ? groupLabels[selectedClient.groupId] : null}
          onClose={() => setSelectedClientId(null)}
          onChangeMonth={moveClient}
          onPatch={patchClient}
          onDelete={removeClient}
          onAddToPipeline={addToPipeline}
          onSaveName={saveClientName}
        />
      )}

      <AnimatePresence>
        {toast && (
          <motion.div
            className={`rv-toast${toast.isError ? ' rv-toast-error' : ''}`}
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.25 }}
          >
            {toast.isError ? <AlertCircle size={15} /> : <CheckCircle size={15} />}
            <span>{toast.message}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
